import { useEffect } from 'react';
import { useCanvasStore } from '../store/canvas-store';
import { useInteraction } from '../chrome/interaction-store';
import { wheelToViewport } from './viewport';

/**
 * App-wide guard against browser pinch-zoom.
 *
 * The canvas gesture handler only preventDefaults ctrl+wheel over the canvas
 * background. A pinch (trackpad pinch = ctrl+wheel) over the chrome — the pill,
 * the Editor Panel, or a chat node (which stops wheel propagation) — therefore
 * reached the browser, which page-zoomed the whole document and scaled the pill
 * menu out of view. This listens in the capture phase on window so it fires no
 * matter where the cursor is (and before any stopPropagation), blocks the page
 * zoom, and routes the gesture to the canvas zoom instead — so pinch-to-zoom
 * works everywhere and the UI never scales.
 */
export function useGlobalZoomGuard() {
  useEffect(() => {
    let idle: ReturnType<typeof setTimeout> | undefined;

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return; // only pinch / ctrl+wheel = zoom intent; let pan/scroll through
      e.preventDefault(); // block the browser from page-zooming the document
      e.stopPropagation(); // prevent the canvas bg handler from also zooming (double speed)

      useInteraction.getState().setPanZoom(true);
      clearTimeout(idle);
      idle = setTimeout(() => useInteraction.getState().setPanZoom(false), 150);

      const vp = useCanvasStore.getState().doc.viewport;
      const next = wheelToViewport(vp, {
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        deltaMode: e.deltaMode,
        ctrlKey: true,
        metaKey: e.metaKey,
        cursor: { x: e.clientX, y: e.clientY },
      });
      useCanvasStore.setState((s) => ({ ...s, doc: { ...s.doc, viewport: next } }));
    };

    // Safari trackpad pinch fires gesture events instead of ctrl+wheel.
    const onGesture = (e: Event) => e.preventDefault();

    const wheelOpts: AddEventListenerOptions = { capture: true, passive: false };
    window.addEventListener('wheel', onWheel, wheelOpts);
    window.addEventListener('gesturestart', onGesture, { passive: false });
    window.addEventListener('gesturechange', onGesture, { passive: false });
    window.addEventListener('gestureend', onGesture, { passive: false });

    return () => {
      clearTimeout(idle);
      window.removeEventListener('wheel', onWheel, wheelOpts);
      window.removeEventListener('gesturestart', onGesture);
      window.removeEventListener('gesturechange', onGesture);
      window.removeEventListener('gestureend', onGesture);
    };
  }, []);
}
